/**
 * The objects on a page: the shapes, the text boxes and the sticky notes (SRS C4).
 *
 * Objects sit above the ink and are painted after it, in z-order. There are tens of
 * them on a busy page rather than the thousands of strokes a three-hour lesson leaves
 * behind, so they are held in a plain list and measured by walking it: an index would
 * cost more to keep in step than it could ever save.
 *
 * Everything that leaves this file leaves as a stroke-shaped view of the object
 * (see object-view.ts), which is how the eraser and the select tool work on all of
 * them without knowing that any of them exist.
 */

import type { BoardObject, Point, Rect, Stroke, Unsubscribe, Viewport } from '@opal/shared';

import { objectStroke } from './object-view.js';
import { layerRank } from './board-layers.js';
import { drawOutline } from './shape-paint.js';
import { isShape, outlinePointsOf } from './shape-object.js';
import { strokeBounds } from './stroke-bounds.js';
import { paintTextObject } from './text-paint.js';

/**
 * The canvas-space box an object covers.
 *
 * A shape's box allows for the width of its line, which is why it is measured from
 * the stroke view rather than from the record: a thick circle covers more board than
 * the circle itself. A text box or a sticky note is exactly its own box.
 */
export function objectBounds(object: BoardObject): Rect {
  if (isShape(object)) return strokeBounds(objectStroke(object));
  return { x: object.x, y: object.y, width: object.width, height: object.height };
}

function overlaps(a: Rect, b: Rect): boolean {
  return (
    a.x <= b.x + b.width &&
    a.x + a.width >= b.x &&
    a.y <= b.y + b.height &&
    a.y + a.height >= b.y
  );
}

/**
 * The order objects are painted in (SRS C8).
 *
 * The layer first — everything on the teacher-only layer sits above everything in the
 * lesson — then z-order within it, then oldest first so the order never wobbles.
 */
function paintOrder(a: BoardObject, b: BoardObject): number {
  return (
    layerRank(a.layer) - layerRank(b.layer) ||
    a.zIndex - b.zIndex ||
    a.createdAt - b.createdAt
  );
}

export interface ObjectBoard {
  /** Every object on the page, in the order they are painted. */
  readonly objects: readonly BoardObject[];
  /** Puts an object on the board, or swaps a changed version of one already there. */
  put(object: BoardObject): void;
  remove(objectId: string): boolean;
  find(objectId: string): BoardObject | null;
  /** The objects whose boxes touch an area, in paint order. */
  near(area: Rect): readonly BoardObject[];
  /** The same, as strokes — how the eraser and the select tool see them. */
  strokesNear(area: Rect): readonly Stroke[];
  /** Canvas-space box around every object, or null when there are none. */
  bounds(): Rect | null;
  paint(ctx: CanvasRenderingContext2D, visible: Rect, viewport: Viewport): void;
  /** Called whenever what is on the board changes. */
  onChanged(handler: () => void): Unsubscribe;
  /**
   * Leaves one object out of the painting, or nothing when given null.
   *
   * Used while a text box is being typed into: the words are then in a real text
   * field over the canvas, and painting them underneath as well would show every
   * letter twice.
   */
  hide(objectId: string | null): void;
}

interface State {
  readonly items: BoardObject[];
  readonly byId: Map<string, BoardObject>;
  readonly handlers: Set<() => void>;
  hidden: string | null;
}

/** The box around every object on the page, or null when there are none. */
function boundsOf(state: State): Rect | null {
  let box: Rect | null = null;
  for (const object of state.items) {
    const bounds = objectBounds(object);
    if (!box) {
      box = bounds;
      continue;
    }
    const x = Math.min(box.x, bounds.x);
    const y = Math.min(box.y, bounds.y);
    const right = Math.max(box.x + box.width, bounds.x + bounds.width);
    const bottom = Math.max(box.y + box.height, bounds.y + bounds.height);
    box = { x, y, width: right - x, height: bottom - y };
  }
  return box;
}

function announce(state: State): void {
  for (const handler of state.handlers) handler();
}

function insert(state: State, object: BoardObject): void {
  const existing = state.byId.get(object.id);
  if (existing) {
    const at = state.items.indexOf(existing);
    if (at >= 0) state.items.splice(at, 1);
  }
  state.byId.set(object.id, object);
  state.items.push(object);
  state.items.sort(paintOrder);
}

function paintOne(
  ctx: CanvasRenderingContext2D,
  object: BoardObject,
  viewport: Viewport,
): void {
  if (!isShape(object)) {
    paintTextObject(ctx, object, viewport);
    return;
  }
  const points: readonly Point[] = outlinePointsOf(object);
  if (points.length === 0) return;
  const view = objectStroke(object);
  drawOutline(
    ctx,
    points,
    { color: view.color, canvasWidth: view.canvasWidth, opacity: 1 },
    viewport,
  );
}

export function createObjectBoard(): ObjectBoard {
  const state: State = {
    items: [],
    byId: new Map<string, BoardObject>(),
    handlers: new Set<() => void>(),
    hidden: null,
  };

  const near = (area: Rect): BoardObject[] =>
    state.items.filter((object) => overlaps(objectBounds(object), area));

  return {
    get objects() {
      return state.items;
    },

    put(object) {
      insert(state, object);
      announce(state);
    },

    remove(objectId) {
      const existing = state.byId.get(objectId);
      if (!existing) return false;
      state.byId.delete(objectId);
      const at = state.items.indexOf(existing);
      if (at >= 0) state.items.splice(at, 1);
      announce(state);
      return true;
    },

    find: (objectId) => state.byId.get(objectId) ?? null,

    near,

    strokesNear: (area) => near(area).map(objectStroke),

    bounds: () => boundsOf(state),

    paint(ctx, visible, viewport) {
      for (const object of near(visible)) {
        if (object.id === state.hidden) continue;
        paintOne(ctx, object, viewport);
      }
    },

    hide(objectId) {
      if (state.hidden === objectId) return;
      state.hidden = objectId;
      announce(state);
    },

    onChanged(handler) {
      state.handlers.add(handler);
      return () => {
        state.handlers.delete(handler);
      };
    },
  };
}
